import { z } from 'zod';
import { taskPrioritySchema } from './tasks.js';

/**
 * AI-proposed drafts. The AI is strictly READ-ONLY at the database layer — it
 * proposes a structured action; the CO actuates it with a click that hits the
 * existing service endpoints (createDm + sendMessage, createTask, updateBoard,
 * updateTask, etc.). These envelopes are emitted by the LLM inside a fenced
 * code block (analogous to `chart-spec`) and parsed in `ai-orchestrator.service`.
 *
 * Phase 4 baseline kinds: `message` (DM/group message), `task` (create task).
 * Phase 4.x extension kinds cover the rest of the task surface — boards, task
 * edits, comments, checklists, statuses, groups, labels — all CRUD operations
 * EXCEPT delete (the CO retains delete authority manually).
 */

export const aiDraftKindEnum = z.enum([
  // Messaging
  'message',
  // Task create (Phase 4 baseline)
  'task',
  // Board CRUD-except-delete
  'board-create',
  'board-rename',
  'board-update',
  'board-archive',
  'board-unarchive',
  'board-add-member',
  'board-remove-member',
  // Task edits
  'task-update',
  'task-status',
  'task-add-assignee',
  'task-remove-assignee',
  'task-set-labels',
  'task-move',
  'task-archive',
  'task-add-comment',
  // Checklist
  'checklist-add',
  'checklist-toggle',
  'checklist-update',
  // Statuses
  'status-create',
  'status-rename',
  'status-recolor',
  // Groups
  'group-create',
  'group-rename',
  // Labels
  'label-create',
  'label-rename',
  'label-recolor',
  // Task update requests (Phase 5)
  'task-request-update',
]);
export type AiDraftKind = z.infer<typeof aiDraftKindEnum>;

export const aiDraftStatusEnum = z.enum(['PENDING', 'SENT', 'CANCELLED']);
export type AiDraftStatus = z.infer<typeof aiDraftStatusEnum>;

const uuid = z.string().uuid();
const shortName = z.string().trim().min(1).max(120);
const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'color must be a #RRGGBB hex string');
const rationale = z.string().trim().min(1).max(500).optional();
const dueDateField = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'dueDate must be YYYY-MM-DD');

// -------------------- Message draft (unchanged) --------------------

export const aiMessageTargetSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('direct'),
    recipientUserId: uuid,
    recipientName: shortName,
  }),
  z.object({
    kind: z.literal('group'),
    conversationId: uuid,
    groupTitle: shortName,
  }),
]);
export type AiMessageTarget = z.infer<typeof aiMessageTargetSchema>;

export const aiMessageDraftSchema = z.object({
  target: aiMessageTargetSchema,
  body: z.string().trim().min(1).max(4000),
  rationale,
  mentionedUserIds: z.array(uuid).max(50).default([]),
});
export type AiMessageDraft = z.infer<typeof aiMessageDraftSchema>;

// -------------------- Task create draft (unchanged) --------------------

export const aiTaskBoardSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('id'), boardId: uuid }),
  z.object({ kind: z.literal('name'), boardName: shortName }),
]);
export type AiTaskBoard = z.infer<typeof aiTaskBoardSchema>;

export const aiTaskDraftSchema = z.object({
  board: aiTaskBoardSchema,
  groupId: uuid.optional(),
  statusId: uuid.optional(),
  title: z.string().trim().min(1).max(200),
  description: z.string().trim().max(4000).optional(),
  priority: taskPrioritySchema.default('NORMAL'),
  dueDate: dueDateField.optional(),
  assigneeUserIds: z.array(uuid).max(20).default([]),
  rationale,
});
export type AiTaskDraft = z.infer<typeof aiTaskDraftSchema>;

// -------------------- Board CRUD (except delete) --------------------

export const aiBoardCreateDraftSchema = z.object({
  name: shortName,
  description: z.string().trim().max(2000).optional(),
  /** Optional members to add after creation. CO is automatically the owner. */
  memberUserIds: z.array(uuid).max(50).default([]),
  rationale,
});
export type AiBoardCreateDraft = z.infer<typeof aiBoardCreateDraftSchema>;

export const aiBoardRenameDraftSchema = z.object({
  boardId: uuid,
  /** Cosmetic — for the confirmation card; server re-derives from boardId. */
  previousName: shortName.optional(),
  name: shortName,
  rationale,
});
export type AiBoardRenameDraft = z.infer<typeof aiBoardRenameDraftSchema>;

export const aiBoardUpdateDraftSchema = z.object({
  boardId: uuid,
  boardName: shortName.optional(),
  description: z.string().trim().max(2000),
  rationale,
});
export type AiBoardUpdateDraft = z.infer<typeof aiBoardUpdateDraftSchema>;

export const aiBoardArchiveDraftSchema = z.object({
  boardId: uuid,
  boardName: shortName.optional(),
  rationale,
});
export type AiBoardArchiveDraft = z.infer<typeof aiBoardArchiveDraftSchema>;

export const aiBoardUnarchiveDraftSchema = z.object({
  boardId: uuid,
  boardName: shortName.optional(),
  rationale,
});
export type AiBoardUnarchiveDraft = z.infer<typeof aiBoardUnarchiveDraftSchema>;

export const aiBoardAddMemberDraftSchema = z.object({
  boardId: uuid,
  boardName: shortName.optional(),
  userId: uuid,
  userName: shortName.optional(),
  rationale,
});
export type AiBoardAddMemberDraft = z.infer<typeof aiBoardAddMemberDraftSchema>;

export const aiBoardRemoveMemberDraftSchema = z.object({
  boardId: uuid,
  boardName: shortName.optional(),
  userId: uuid,
  userName: shortName.optional(),
  rationale,
});
export type AiBoardRemoveMemberDraft = z.infer<typeof aiBoardRemoveMemberDraftSchema>;

// -------------------- Task edits --------------------

/**
 * Multi-field task update — title, description, priority, due date, group.
 * Only the fields the CO wants changed need to be present; null on dueDate
 * clears it. Status, assignees and labels have dedicated envelopes because
 * each maps to a separate endpoint with its own semantics.
 */
export const aiTaskUpdateDraftSchema = z
  .object({
    taskId: uuid,
    taskTitle: shortName.optional(),
    title: z.string().trim().min(1).max(200).optional(),
    description: z.string().trim().max(4000).optional(),
    priority: taskPrioritySchema.optional(),
    dueDate: dueDateField.nullable().optional(),
    groupId: uuid.optional(),
    rationale,
  })
  .refine(
    (v) =>
      v.title !== undefined ||
      v.description !== undefined ||
      v.priority !== undefined ||
      v.dueDate !== undefined ||
      v.groupId !== undefined,
    { message: 'task-update must change at least one field.' },
  );
export type AiTaskUpdateDraft = z.infer<typeof aiTaskUpdateDraftSchema>;

export const aiTaskStatusDraftSchema = z.object({
  taskId: uuid,
  taskTitle: shortName.optional(),
  statusId: uuid,
  statusName: shortName.optional(),
  rationale,
});
export type AiTaskStatusDraft = z.infer<typeof aiTaskStatusDraftSchema>;

export const aiTaskAddAssigneeDraftSchema = z.object({
  taskId: uuid,
  taskTitle: shortName.optional(),
  userId: uuid,
  userName: shortName.optional(),
  rationale,
});
export type AiTaskAddAssigneeDraft = z.infer<typeof aiTaskAddAssigneeDraftSchema>;

export const aiTaskRemoveAssigneeDraftSchema = z.object({
  taskId: uuid,
  taskTitle: shortName.optional(),
  userId: uuid,
  userName: shortName.optional(),
  rationale,
});
export type AiTaskRemoveAssigneeDraft = z.infer<typeof aiTaskRemoveAssigneeDraftSchema>;

export const aiTaskSetLabelsDraftSchema = z.object({
  taskId: uuid,
  taskTitle: shortName.optional(),
  labelIds: z.array(uuid).max(20),
  /** Optional — names shown on the confirmation card. */
  labelNames: z.array(shortName).max(20).optional(),
  rationale,
});
export type AiTaskSetLabelsDraft = z.infer<typeof aiTaskSetLabelsDraftSchema>;

export const aiTaskMoveDraftSchema = z.object({
  taskId: uuid,
  taskTitle: shortName.optional(),
  groupId: uuid,
  groupName: shortName.optional(),
  statusId: uuid.optional(),
  rationale,
});
export type AiTaskMoveDraft = z.infer<typeof aiTaskMoveDraftSchema>;

export const aiTaskArchiveDraftSchema = z.object({
  taskId: uuid,
  taskTitle: shortName.optional(),
  rationale,
});
export type AiTaskArchiveDraft = z.infer<typeof aiTaskArchiveDraftSchema>;

export const aiTaskAddCommentDraftSchema = z.object({
  taskId: uuid,
  taskTitle: shortName.optional(),
  body: z.string().trim().min(1).max(4000),
  mentionedUserIds: z.array(uuid).max(50).default([]),
  rationale,
});
export type AiTaskAddCommentDraft = z.infer<typeof aiTaskAddCommentDraftSchema>;

// -------------------- Checklist --------------------

export const aiChecklistAddDraftSchema = z.object({
  taskId: uuid,
  taskTitle: shortName.optional(),
  text: z.string().trim().min(1).max(280),
  rationale,
});
export type AiChecklistAddDraft = z.infer<typeof aiChecklistAddDraftSchema>;

export const aiChecklistToggleDraftSchema = z.object({
  itemId: uuid,
  itemText: shortName.optional(),
  isComplete: z.boolean(),
  rationale,
});
export type AiChecklistToggleDraft = z.infer<typeof aiChecklistToggleDraftSchema>;

export const aiChecklistUpdateDraftSchema = z.object({
  itemId: uuid,
  previousText: shortName.optional(),
  text: z.string().trim().min(1).max(280),
  rationale,
});
export type AiChecklistUpdateDraft = z.infer<typeof aiChecklistUpdateDraftSchema>;

// -------------------- Statuses --------------------

export const aiStatusCreateDraftSchema = z.object({
  boardId: uuid,
  boardName: shortName.optional(),
  name: shortName,
  color: hexColor,
  isDone: z.boolean().default(false),
  rationale,
});
export type AiStatusCreateDraft = z.infer<typeof aiStatusCreateDraftSchema>;

export const aiStatusRenameDraftSchema = z.object({
  statusId: uuid,
  previousName: shortName.optional(),
  name: shortName,
  rationale,
});
export type AiStatusRenameDraft = z.infer<typeof aiStatusRenameDraftSchema>;

export const aiStatusRecolorDraftSchema = z.object({
  statusId: uuid,
  statusName: shortName.optional(),
  color: hexColor,
  rationale,
});
export type AiStatusRecolorDraft = z.infer<typeof aiStatusRecolorDraftSchema>;

// -------------------- Groups --------------------

export const aiGroupCreateDraftSchema = z.object({
  boardId: uuid,
  boardName: shortName.optional(),
  name: shortName,
  rationale,
});
export type AiGroupCreateDraft = z.infer<typeof aiGroupCreateDraftSchema>;

export const aiGroupRenameDraftSchema = z.object({
  groupId: uuid,
  previousName: shortName.optional(),
  name: shortName,
  rationale,
});
export type AiGroupRenameDraft = z.infer<typeof aiGroupRenameDraftSchema>;

// -------------------- Labels --------------------

export const aiLabelCreateDraftSchema = z.object({
  boardId: uuid,
  boardName: shortName.optional(),
  name: shortName,
  color: hexColor,
  rationale,
});
export type AiLabelCreateDraft = z.infer<typeof aiLabelCreateDraftSchema>;

export const aiLabelRenameDraftSchema = z.object({
  labelId: uuid,
  previousName: shortName.optional(),
  name: shortName,
  rationale,
});
export type AiLabelRenameDraft = z.infer<typeof aiLabelRenameDraftSchema>;

export const aiLabelRecolorDraftSchema = z.object({
  labelId: uuid,
  labelName: shortName.optional(),
  color: hexColor,
  rationale,
});
export type AiLabelRecolorDraft = z.infer<typeof aiLabelRecolorDraftSchema>;

// -------------------- Task update request (Phase 5) --------------------

/**
 * CO asks one or more officers for a status update on a specific task. The
 * dispatched action hits the existing `POST /tasks/:taskId/updates/request`
 * route; the AI never writes to the DB itself.
 */
export const aiTaskRequestUpdateDraftSchema = z.object({
  taskId: uuid,
  taskTitle: shortName.optional(),
  boardName: shortName.optional(),
  targetUserIds: z.array(uuid).min(1).max(20),
  notes: z.string().trim().max(500).optional(),
  rationale,
});
export type AiTaskRequestUpdateDraft = z.infer<typeof aiTaskRequestUpdateDraftSchema>;

// -------------------- Dispatch endpoint --------------------

/**
 * Body for `PATCH /ai/conversations/:convoId/messages/:msgId/draft`. The CO's
 * frontend flips a PENDING draft to SENT after a successful dispatch (carrying
 * the refId of the resulting Message / Task / Board / etc. row) or to
 * CANCELLED on dismissal.
 */
export const dispatchDraftRequestSchema = z
  .object({
    status: z.enum(['SENT', 'CANCELLED']),
    dispatchedRefId: uuid.optional(),
  })
  .refine((v) => v.status !== 'SENT' || Boolean(v.dispatchedRefId), {
    message: 'dispatchedRefId is required when status is SENT.',
    path: ['dispatchedRefId'],
  });
export type DispatchDraftRequest = z.infer<typeof dispatchDraftRequestSchema>;

// -------------------- Schema lookup by kind --------------------

/**
 * Lookup table from `draftKind` → payload schema. Used by `extractDraft` to
 * validate by kind; if the kind isn't in this table, the envelope is rejected.
 */
export const aiDraftSchemaByKind = {
  message: aiMessageDraftSchema,
  task: aiTaskDraftSchema,
  'board-create': aiBoardCreateDraftSchema,
  'board-rename': aiBoardRenameDraftSchema,
  'board-update': aiBoardUpdateDraftSchema,
  'board-archive': aiBoardArchiveDraftSchema,
  'board-unarchive': aiBoardUnarchiveDraftSchema,
  'board-add-member': aiBoardAddMemberDraftSchema,
  'board-remove-member': aiBoardRemoveMemberDraftSchema,
  'task-update': aiTaskUpdateDraftSchema,
  'task-status': aiTaskStatusDraftSchema,
  'task-add-assignee': aiTaskAddAssigneeDraftSchema,
  'task-remove-assignee': aiTaskRemoveAssigneeDraftSchema,
  'task-set-labels': aiTaskSetLabelsDraftSchema,
  'task-move': aiTaskMoveDraftSchema,
  'task-archive': aiTaskArchiveDraftSchema,
  'task-add-comment': aiTaskAddCommentDraftSchema,
  'checklist-add': aiChecklistAddDraftSchema,
  'checklist-toggle': aiChecklistToggleDraftSchema,
  'checklist-update': aiChecklistUpdateDraftSchema,
  'status-create': aiStatusCreateDraftSchema,
  'status-rename': aiStatusRenameDraftSchema,
  'status-recolor': aiStatusRecolorDraftSchema,
  'group-create': aiGroupCreateDraftSchema,
  'group-rename': aiGroupRenameDraftSchema,
  'label-create': aiLabelCreateDraftSchema,
  'label-rename': aiLabelRenameDraftSchema,
  'label-recolor': aiLabelRecolorDraftSchema,
  'task-request-update': aiTaskRequestUpdateDraftSchema,
} as const;

/** Any concrete draft payload. Indexed by `draftKind` on the AiMessage. */
export type AiDraftPayloadByKind = {
  [K in keyof typeof aiDraftSchemaByKind]: z.infer<(typeof aiDraftSchemaByKind)[K]>;
};

/** Discriminated wrapper used in transport (assistant envelope: `{ kind, ...payload }`). */
export type AiDraftEnvelope = {
  [K in keyof AiDraftPayloadByKind]: { kind: K; payload: AiDraftPayloadByKind[K] };
}[keyof AiDraftPayloadByKind];
